/**
 * Email Delivery Events — super_admin diagnostic dashboard.
 *
 * Surfaces Brevo's /v3/smtp/statistics/events API inside the app so
 * "user X didn't get their OTP" reports can be resolved in seconds.
 *
 * Backend: GET /api/admin/email/events?email=<>&limit=<>&days=<>
 */
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Mail, Search, RefreshCcw, AlertCircle, CheckCircle2, EyeOff, Clock, Ban, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const EVENT_META = {
  requests:        { label: "Submitted",   color: "bg-slate-100 text-slate-700", icon: Mail },
  delivered:       { label: "Delivered",   color: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  opened:          { label: "Opened",      color: "bg-blue-50 text-blue-700", icon: CheckCircle2 },
  uniqueOpened:    { label: "Unique-open", color: "bg-blue-50 text-blue-700", icon: CheckCircle2 },
  clicks:          { label: "Clicked",     color: "bg-indigo-50 text-indigo-700", icon: CheckCircle2 },
  uniqueClicks:    { label: "Unique-click",color: "bg-indigo-50 text-indigo-700", icon: CheckCircle2 },
  loadedByProxy:   { label: "Proxy-loaded",color: "bg-slate-50 text-slate-600", icon: CheckCircle2 },
  hardBounces:     { label: "Hard bounce", color: "bg-red-50 text-red-700", icon: Ban },
  softBounces:     { label: "Soft bounce", color: "bg-amber-50 text-amber-700", icon: AlertCircle },
  blocked:         { label: "Blocked",     color: "bg-red-50 text-red-700", icon: Ban },
  deferred:        { label: "Deferred",    color: "bg-amber-50 text-amber-700", icon: Clock },
  invalid:         { label: "Invalid",     color: "bg-red-50 text-red-700", icon: Ban },
  spam:            { label: "Spam-marked", color: "bg-red-50 text-red-700", icon: AlertCircle },
  complaints:      { label: "Complaint",   color: "bg-red-50 text-red-700", icon: AlertCircle },
  unsubscribed:    { label: "Unsubscribed",color: "bg-slate-100 text-slate-700", icon: EyeOff },
};

function eventMeta(name) {
  return EVENT_META[name] || { label: name, color: "bg-slate-100 text-slate-700", icon: Mail };
}

export default function EmailEventsPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(50);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  if (!user || user.role !== "super_admin") {
    return (
      <div className="p-10 text-center text-sm text-slate-600">
        This page is reserved for platform administrators.
      </div>
    );
  }

  const search = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setBusy(true);
    setData(null);
    try {
      const { data: resp } = await api.get("/admin/email/events", {
        params: { email: email.trim(), limit, days },
      });
      setData(resp);
      const totalEvents = (resp.events || []).length;
      toast.success(`${totalEvents} event${totalEvents === 1 ? "" : "s"} in the last ${days} days`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Failed to load events: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const summary = data?.summary || {};
  const events = data?.events || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F6F4EE] to-[#EDE7DA] py-10 px-4 sm:px-6 lg:px-10" data-testid="admin-email-events-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold text-[#0F4C81] hover:underline" data-testid="back-to-dashboard">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>
        <header className="mt-4 mb-8">
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold">Security · Email diagnostics</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">Email delivery events</h1>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-2xl">
            Live view of Brevo&apos;s transactional event stream for any recipient.
            Use this to triage &ldquo;I didn&apos;t get my code&rdquo; tickets in seconds — every
            email we send is logged with a delivered / opened / blocked / deferred event.
          </p>
        </header>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Search by recipient</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={search} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-7">
                <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Recipient email</Label>
                <Input
                  id="email" type="email" required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="someone@example.com"
                  className="mt-1.5 h-10"
                  data-testid="email-events-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="days" className="text-xs font-semibold text-slate-700">Days back</Label>
                <Input
                  id="days" type="number" min={1} max={180}
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value) || 30)}
                  className="mt-1.5 h-10"
                  data-testid="email-events-days"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="limit" className="text-xs font-semibold text-slate-700">Max events</Label>
                <Input
                  id="limit" type="number" min={1} max={200}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value) || 50)}
                  className="mt-1.5 h-10"
                  data-testid="email-events-limit"
                />
              </div>
              <div className="sm:col-span-1">
                <Button
                  type="submit"
                  disabled={busy}
                  className="h-10 w-full bg-[#0F4C81] hover:bg-[#0A3860] text-white text-xs font-semibold uppercase tracking-[0.14em]"
                  data-testid="email-events-submit"
                >
                  {busy ? <RefreshCcw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {data && (
          <>
            <Card className="mb-6" data-testid="email-events-summary">
              <CardHeader>
                <CardTitle className="text-base">Summary · last {data.lookback_days} days</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(summary).length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="email-events-empty">
                    No events for <span className="font-mono">{data.email}</span> in this window.
                    The email is either not registered with Brevo OR none of your sends went to this address.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(summary).map(([name, count]) => {
                      const meta = eventMeta(name);
                      const Icon = meta.icon;
                      return (
                        <Badge key={name} variant="outline" className={`${meta.color} border-transparent`}>
                          <Icon className="h-3 w-3 mr-1.5" />
                          <span className="font-semibold mr-1.5">{count}</span>
                          {meta.label}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="email-events-list">
              <CardHeader>
                <CardTitle className="text-base">Recent events · {events.length}</CardTitle>
              </CardHeader>
              <CardContent>
                {events.length === 0 ? (
                  <p className="text-sm text-slate-500">No events to show.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b">
                          <th className="py-2 pr-3">When</th>
                          <th className="py-2 pr-3">Event</th>
                          <th className="py-2 pr-3">Subject</th>
                          <th className="py-2 pr-3">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((ev, idx) => {
                          const meta = eventMeta(ev.event);
                          const Icon = meta.icon;
                          return (
                            <tr key={idx} className="border-b border-slate-100 last:border-none align-top" data-testid={`email-event-row-${idx}`}>
                              <td className="py-2.5 pr-3 text-xs text-slate-500 whitespace-nowrap font-mono">
                                {ev.date ? new Date(ev.date).toLocaleString() : "—"}
                              </td>
                              <td className="py-2.5 pr-3">
                                <Badge variant="outline" className={`${meta.color} border-transparent`}>
                                  <Icon className="h-3 w-3 mr-1.5" />
                                  {meta.label}
                                </Badge>
                              </td>
                              <td className="py-2.5 pr-3 text-slate-700 max-w-md truncate">
                                {ev.subject || <span className="text-slate-400 italic">—</span>}
                              </td>
                              <td className="py-2.5 pr-3 text-xs text-slate-500">
                                {ev.reason || <span className="text-slate-300">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
